import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  MobileStepper,
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import BubbleCloud from './BubbleCloud';

const STORAGE_KEY = 'evcsOnboardingComplete';

// Module-level singleton — survives re-renders and guarantees only one intro
// track is ever playing at a time, even if the carousel is opened/closed rapidly.
let _introAudio     = null;   // the live HTMLAudioElement
let _introCtx       = null;   // AudioContext (for iOS-safe GainNode volume control)
let _introGain      = null;   // GainNode — iOS cannot reset gain.value unlike audio.volume
let _introFadeTimer = null;   // current fade setInterval id
let _finishCalled   = false;  // prevents open=false useEffect from cancelling finish()'s fade

function _clearFadeTimer() {
  if (_introFadeTimer !== null) {
    console.log('[INTRO-AUDIO] clearFadeTimer — clearing interval', _introFadeTimer);
    clearInterval(_introFadeTimer);
    _introFadeTimer = null;
  }
}

/** Returns current effective volume (GainNode if available, else audio.volume). */
function _getVol() {
  if (_introGain) return _introGain.gain.value;
  return _introAudio?.volume ?? 0;
}

/** Sets effective volume. */
function _setVol(v) {
  if (_introGain) { _introGain.gain.value = v; return; }
  if (_introAudio) _introAudio.volume = v;
}

/** Hard-stops and discards the current audio with no fade. */
function _hardStopIntro() {
  console.log('[INTRO-AUDIO] _hardStopIntro — audio:', !!_introAudio, 'vol:', _introAudio ? _getVol().toFixed(3) : 'n/a');
  _clearFadeTimer();
  if (_introAudio) {
    _introAudio.pause();
    _introAudio.src = '';
    _introAudio = null;
  }
  if (_introCtx) {
    try { _introCtx.close(); } catch (_) {}
    _introCtx = null;
    _introGain = null;
  }
  console.log('[INTRO-AUDIO] _hardStopIntro done');
}

/** Start intro music. Any currently-playing/fading instance is killed first. */
function startIntroMusic() {
  console.log('[INTRO-AUDIO] startIntroMusic called — killing any existing audio first');
  _hardStopIntro();

  const audio = new Audio('/sounds/intro.mp3');
  audio.loop    = true;
  audio.preload = 'auto';
  _introAudio   = audio;

  // Use Web Audio API GainNode for volume control — iOS WKWebView resets
  // audio.volume to 1.0 after each loop, but gain.value is immune to that.
  try {
    const ctx  = new (window.AudioContext || window.webkitAudioContext)();
    const gain = ctx.createGain();
    gain.gain.value = 0;
    ctx.createMediaElementSource(audio).connect(gain);
    gain.connect(ctx.destination);
    _introCtx  = ctx;
    _introGain = gain;
    console.log('[INTRO-AUDIO] Web Audio GainNode connected');
  } catch (e) {
    console.warn('[INTRO-AUDIO] Web Audio API unavailable, falling back to audio.volume:', e.message);
    audio.volume = 0;
  }

  audio.play().catch((e) => console.warn('[INTRO-AUDIO] play() blocked:', e));

  // Fade in to 0.30 over ~1200ms
  const TARGET = 0.30;
  _introFadeTimer = setInterval(() => {
    if (audio !== _introAudio) { console.log('[INTRO-AUDIO] fade-in superseded — bailing'); _clearFadeTimer(); return; }
    const curr = _getVol();
    if (curr < TARGET - 0.01) {
      _setVol(Math.min(TARGET, curr + 0.02));
    } else {
      _setVol(TARGET);
      console.log('[INTRO-AUDIO] fade-in complete, vol =', _getVol().toFixed(3));
      _clearFadeTimer();
    }
  }, 80);
}

/** Fade out and stop using a step counter (immune to iOS audio.volume resets).
 *  Uses GainNode when available for a smooth audible fade on iOS.
 *  _introAudio stays set until fully done so startIntroMusic can hard-stop
 *  a running fade if the tour is re-opened during fadeout. */
function stopIntroMusic(fadeDuration = 1200) {
  console.log('[INTRO-AUDIO] stopIntroMusic called — fadeDuration:', fadeDuration,
    'audio:', !!_introAudio, 'vol:', _introAudio ? _getVol().toFixed(3) : 'n/a',
    '_finishCalled:', _finishCalled);
  _clearFadeTimer();
  if (!_introAudio) {
    console.log('[INTRO-AUDIO] stopIntroMusic — no audio to stop, returning');
    return;
  }

  if (fadeDuration === 0) {
    console.log('[INTRO-AUDIO] fadeDuration=0 — hard stop immediately');
    _hardStopIntro();
    return;
  }

  const audio    = _introAudio;
  const startVol = _getVol();
  const totalSteps = Math.max(1, Math.round(fadeDuration / 80));
  let stepsRemaining = totalSteps;
  console.log('[INTRO-AUDIO] starting fade-out — vol:', startVol.toFixed(3),
    'steps:', totalSteps, 'duration:', fadeDuration + 'ms',
    'gainNode:', !!_introGain);

  _introFadeTimer = setInterval(() => {
    if (audio !== _introAudio) { console.log('[INTRO-AUDIO] fade-out superseded — bailing'); _clearFadeTimer(); return; }
    stepsRemaining--;
    _setVol(Math.max(0, (stepsRemaining / totalSteps) * startVol));
    if (stepsRemaining <= 0) {
      console.log('[INTRO-AUDIO] fade-out counter reached 0 — hard stopping');
      _hardStopIntro();
    }
  }, 80);
}

const slides = [
  {
    emoji: '🎵',
    title: 'Welcome to Every Voice Can Sing',
    body: 'Learn Indian classical singing through fun, interactive games. No prior experience needed — just your voice!',
    sub: 'Train your ear • Build your pitch • Earn stars',
    bg: 'linear-gradient(135deg, #58CC02 0%, #4DB802 100%)',
    textColor: '#fff',
  },
  {
    emoji: '🎼',
    title: 'What is "Sa"?',
    body: 'Sa is the home note of Indian classical music — like "Do" in Do-Re-Mi. Every melody returns home to Sa.',
    sub: 'Sa · Re · Ga · Ma · Pa · Dha · Ni · Sa',
    bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    textColor: '#fff',
    noteRow: true,
  },
  {
    emoji: '🎤',
    title: 'First: Calibrate Your Voice',
    body: 'Everyone sings at a different pitch. We\'ll listen to your natural Sa and tune the app to match your voice perfectly.',
    sub: 'Takes about 5 seconds. You can re-calibrate anytime from Settings.',
    bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    textColor: '#fff',
  },
  {
    type: 'journeyPicker',
    bg: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)',
    textColor: '#fff',
  },
];

const NOTES = ['Sa', 'Re', 'Ga', 'Ma', 'Pa', 'Dha', 'Ni'];

/**
 * OnboardingCarousel — full-screen dialog shown to first-time users.
 *
 * Props:
 *   open                {boolean}
 *   onComplete          {function(journeyTags)}  called when user finishes; receives selected tag ids (may be [])
 *   initialJourneyTags  {string[]}              pre-selected tags from saved preferences (for re-opens)
 */
const OnboardingCarousel = ({ open, onComplete, initialJourneyTags = [] }) => {
  const [activeStep, setActiveStep] = useState(0);
  const maxSteps = slides.length;
  const touchStartX = useRef(null);

  // Start intro music when carousel opens; stop when it closes.
  // startIntroMusic() hard-stops any existing audio synchronously, so
  // spamming "Show App Tour" never accumulates multiple Audio instances.
  // When finish() is the one closing the dialog, _finishCalled is true and
  // we skip the hard-stop here so the 800ms fade from finish() runs undisturbed.
  useEffect(() => {
    if (open) {
      _finishCalled = false;
      console.log('[INTRO-AUDIO] useEffect open=true — scheduling startIntroMusic in 300ms');
      const t = setTimeout(() => startIntroMusic(), 300);
      return () => clearTimeout(t);
    } else {
      console.log('[INTRO-AUDIO] useEffect open=false — _finishCalled:', _finishCalled);
      if (_finishCalled) {
        // finish() already started a timed fade — don't interfere
        console.log('[INTRO-AUDIO] useEffect: finish() owns the fade, skipping');
        _finishCalled = false;
      } else {
        // Dialog was closed by some other means (back button, etc.) — hard stop
        console.log('[INTRO-AUDIO] useEffect: no finish() — hard stopping');
        _hardStopIntro();
      }
    }
  }, [open]);


  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (delta < -50 && activeStep < maxSteps - 1) {
      import('../utils/audioEngine').then(({ playSlideChime }) => playSlideChime());
      setActiveStep((p) => p + 1);
    }
    if (delta >  50 && activeStep > 0) {
      import('../utils/audioEngine').then(({ playSlideChime }) => playSlideChime());
      setActiveStep((p) => p - 1);
    }
  };

  const handleNext = () => {
    if (activeStep === maxSteps - 1) {
      finish();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const finish = (tags = []) => {
    console.log('[INTRO-AUDIO] finish() called — vol:', _introAudio?.volume?.toFixed(3), 'audio:', !!_introAudio);
    _finishCalled = true;
    stopIntroMusic(800);
    localStorage.setItem(STORAGE_KEY, 'true');
    onComplete(tags);
  };

  const slide = slides[activeStep];

  return (
    <Dialog
      open={open}
      fullScreen
      keepMounted={false}
      // Force Paper to cover the ENTIRE physical screen including safe-area insets.
      // Using position:fixed + inset:0 is more reliable than height:'100%' on iOS WKWebView.
      PaperProps={{
        sx: {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          m: 0,
          borderRadius: 0,
          background: slide.bg,
          transition: 'background 0.4s ease',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 0,
          // Fill the whole Paper; safe-area padding pushes content inward but bg covers everything.
          height: '100%',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          background: slide.bg,
          transition: 'background 0.4s ease',
          overflow: 'hidden',
        }}
      >
        {/* Skip */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', pt: '12px', px: 2, flexShrink: 0 }}>
          <Button
            onClick={finish}
            sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', textTransform: 'none' }}
          >
            Skip
          </Button>
        </Box>

        {/* Slide viewport — pure-CSS, no react-swipeable-views */}
        <Box sx={{ flex: 1, width: '100%', overflow: 'hidden' }} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          <Box
            sx={{
              display: 'flex',
              width: `${maxSteps * 100}%`,
              height: '100%',
              transform: `translateX(-${(activeStep * 100) / maxSteps}%)`,
              transition: 'transform 0.35s ease',
            }}
          >
            {slides.map((s, index) => (
              <Box
                key={index}
                sx={{
                  width: `${100 / maxSteps}%`,
                  flexShrink: 0,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: s.type === 'journeyPicker' ? 'flex-start' : 'center',
                  px: s.type === 'journeyPicker' ? 1 : 4,
                  py: s.type === 'journeyPicker' ? 0 : 2,
                  textAlign: 'center',
                  color: '#fff',
                  overflow: 'hidden',
                }}
              >
                {s.type === 'journeyPicker' ? (
                  /* Journey bubble picker — BubbleCloud fills this slide slot */
                  <BubbleCloud
                    initialSelected={initialJourneyTags}
                    onConfirm={(tags) => finish(tags)}
                  />
                ) : (
                  <>
                    {/* Emoji */}
                    <Box sx={{ fontSize: '5rem', mb: 3 }}>{s.emoji}</Box>

                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 'bold', mb: 2, lineHeight: 1.2, color: '#fff' }}
                    >
                      {s.title}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{ mb: 3, opacity: 0.92, maxWidth: 340, lineHeight: 1.6, color: '#fff' }}
                    >
                      {s.body}
                    </Typography>

                    {/* Note display row for slide 2 */}
                    {s.noteRow && (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          flexWrap: 'wrap',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        {NOTES.map((note, i) => (
                          <Box
                            key={note}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255,255,255,0.25)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '0.85rem',
                              color: '#fff',
                            }}
                          >
                            {note}
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Typography
                      variant="body2"
                      sx={{ opacity: 0.75, fontStyle: 'italic', maxWidth: 300, color: '#fff' }}
                    >
                      {s.sub}
                    </Typography>
                  </>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Stepper + buttons */}
        <Box sx={{ width: '100%', flexShrink: 0, pb: 'calc(env(safe-area-inset-bottom, 0px) + 24px)' }}>
          <MobileStepper
            variant="dots"
            steps={maxSteps}
            position="static"
            activeStep={activeStep}
            sx={{
              background: 'transparent',
              '& .MuiMobileStepper-dot': { backgroundColor: 'rgba(255,255,255,0.4)' },
              '& .MuiMobileStepper-dotActive': { backgroundColor: '#fff' },
            }}
            nextButton={
              /* On the journey slide the BubbleCloud supplies its own CTA — hide this button */
              slides[activeStep]?.type === 'journeyPicker' ? (
                <Box sx={{ minWidth: 100 }} />
              ) : (
                <Button
                  size="large"
                  onClick={handleNext}
                  sx={{
                    color: '#fff',
                    fontWeight: 'bold',
                    minWidth: 100,
                    ...(activeStep === maxSteps - 1 && {
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      borderRadius: 8,
                      px: 2,
                    }),
                  }}
                >
                  {activeStep === maxSteps - 1 ? "Let's Go 🎤" : 'Next'}
                  {activeStep < maxSteps - 1 && <KeyboardArrowRightIcon />}
                </Button>
              )
            }
            backButton={
              <Button
                size="large"
                onClick={handleBack}
                disabled={activeStep === 0}
                sx={{ color: 'rgba(255,255,255,0.7)' }}
              >
                <KeyboardArrowLeftIcon />
                Back
              </Button>
            }
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

/** Returns true if the onboarding has already been completed. */
export const hasCompletedOnboarding = () =>
  localStorage.getItem(STORAGE_KEY) === 'true';

export default OnboardingCarousel;
